import React from 'react';
import { motion } from 'framer-motion';
import { RevisionSummary } from '@/hooks/useRevisionLog';
import styles from '../ShutdownRitual.module.css';

interface RevisionSummaryStepProps {
  summary: RevisionSummary;
  onViewFullHistory?: () => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function RevisionSummaryStep({ summary, onViewFullHistory }: RevisionSummaryStepProps) {
  const {
    totalRevisions,
    moveCount,
    deleteCount,
    resizeCount,
    editCount,
    addCount,
    mostRevisedBlock,
  } = summary;

  const hasRevisions = totalRevisions > 0;

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Today's Schedule Review
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        {hasRevisions
          ? `You revised your plan ${totalRevisions} time${totalRevisions === 1 ? '' : 's'} today`
          : 'You stuck to your original plan today - impressive focus!'}
      </motion.p>

      {hasRevisions && (
        <>
          {/* Revision Stats Table */}
          <motion.div className={styles.revisionStatsTable} variants={itemVariants}>
            <div className={styles.revisionStatRow}>
              <span className={styles.revisionStatIcon} aria-hidden="true">
                &#8635;
              </span>
              <span className={styles.revisionStatLabel}>Blocks moved</span>
              <span className={styles.revisionStatValue}>{moveCount}</span>
            </div>
            <div className={styles.revisionStatRow}>
              <span className={styles.revisionStatIcon} aria-hidden="true">
                &#10005;
              </span>
              <span className={styles.revisionStatLabel}>Blocks removed</span>
              <span className={styles.revisionStatValue}>{deleteCount}</span>
            </div>
            <div className={styles.revisionStatRow}>
              <span className={styles.revisionStatIcon} aria-hidden="true">
                &#8596;
              </span>
              <span className={styles.revisionStatLabel}>Blocks resized</span>
              <span className={styles.revisionStatValue}>{resizeCount}</span>
            </div>
            <div className={styles.revisionStatRow}>
              <span className={styles.revisionStatIcon} aria-hidden="true">
                &#9998;
              </span>
              <span className={styles.revisionStatLabel}>Blocks edited</span>
              <span className={styles.revisionStatValue}>{editCount}</span>
            </div>
            <div className={styles.revisionStatRow}>
              <span className={styles.revisionStatIcon} aria-hidden="true">
                +
              </span>
              <span className={styles.revisionStatLabel}>Blocks added</span>
              <span className={styles.revisionStatValue}>{addCount}</span>
            </div>
          </motion.div>

          {/* Most Revised Block */}
          {mostRevisedBlock && mostRevisedBlock.count > 1 && (
            <motion.div className={styles.mostRevisedBlock} variants={itemVariants}>
              <p className={styles.mostRevisedLabel}>Most revised block</p>
              <p className={styles.mostRevisedTitle}>"{mostRevisedBlock.blockTitle}"</p>
              <p className={styles.mostRevisedCount}>Revised {mostRevisedBlock.count} times</p>
            </motion.div>
          )}
        </>
      )}

      {/* Newport Quote */}
      <motion.div className={styles.newportQuote} variants={itemVariants}>
        <p className={styles.newportQuoteText}>
          {hasRevisions
            ? '"Frequent adjustment is normal. The value of time blocking isn\'t that you guess your day perfectly in advance, but that you always have a plan."'
            : '"When you time block, you give every minute of your day a job. This level of organization ensures that you\'re always working on what matters most."'}
        </p>
        <p className={styles.newportQuoteAuthor}>- Cal Newport</p>
      </motion.div>

      {/* View Full History Button */}
      {hasRevisions && onViewFullHistory && (
        <motion.button
          className={styles.viewHistoryBtn}
          onClick={onViewFullHistory}
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label="View full revision history"
        >
          View Full History
        </motion.button>
      )}

      {/* Empty State */}
      {!hasRevisions && (
        <motion.div className={styles.emptyState} variants={itemVariants}>
          <p>
            No revisions recorded for today. This could mean you planned well or haven't made
            changes yet.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default RevisionSummaryStep;
